import { Decimal } from 'decimal.js';

import { AvailableRegions } from '../model/Project';

const defaultLocale = 'en-GB';
const defaultCurrency = 'EUR';

// #FIXME: move this global state somewhere else. It doesn't belong to lib/utils
// The formatters below default their `currency` parameter to this variable, so it
// must not share the parameter's name (a default cannot reference itself).
let locale = defaultLocale;
let currentCurrency = defaultCurrency;

export function setNumberFormatLocale(l: string) {
  locale = l;
}

export function getNumberFormatLocale() {
  return locale;
}

export function setCurrency(c: string) {
  currentCurrency = c;
}

export function getCurrency() {
  return currentCurrency;
}

export function guessNumberFormatLocaleForRegion(region: AvailableRegions | undefined) {
  switch (region) {
    case AvailableRegions.EU:
      return 'de';
    case AvailableRegions.US_EAST:
      return 'en-US';
    case AvailableRegions.BR:
      return 'en-US';
    case AvailableRegions.AU:
      return 'en-AU';
    case AvailableRegions.JP:
      return 'en-GB';
    case AvailableRegions.UK:
      return 'en-GB';
    default:
      return defaultLocale;
  }
}

export function getCurrencySymbol(currency = currentCurrency) {
  let formatter: Intl.NumberFormat;
  try {
    formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    });
  } catch {
    // Intl throws RangeError on an empty/invalid (non-ISO-4217) code — e.g. a
    // misconfigured tenant currency. Degrade to no symbol rather than letting
    // it bubble up and 500 the whole Excel export.
    return '';
  }

  const parts = formatter.formatToParts(1);
  if (parts.length === 0) {
    return '';
  }

  const first = parts[0];
  if (first?.type === 'currency') {
    return first.value;
  }

  return parts.find((part) => part.type === 'currency')?.value || '';
}

// Cached per locale: the parser asks for this on every keystroke and every row render.
const decimalSeparators = new Map<string, string>();

export function getDecimalSeparator(overrideLocale = locale) {
  let separator = decimalSeparators.get(overrideLocale);
  if (separator === undefined) {
    separator = new Intl.NumberFormat(overrideLocale).formatToParts(1.1)[1]?.value ?? '.';
    decimalSeparators.set(overrideLocale, separator);
  }

  return separator;
}

/**
 * Builds a currency formatter; a malformed currency code (an empty or misconfigured
 * tenant setting) degrades to a plain number formatter with the same fraction digits
 * instead of throwing out of every money cell. An invalid locale still throws from
 * the fallback: locales come from the region table, currencies from tenant config.
 */
function moneyFormatter(
  currency: string | null | undefined,
  overrideLocale: string,
  digits: Pick<Intl.NumberFormatOptions, 'minimumFractionDigits' | 'maximumFractionDigits'>,
) {
  if (currency) {
    try {
      return new Intl.NumberFormat(overrideLocale, { style: 'currency', currency, ...digits });
    } catch {
      // Not a well-formed ISO 4217 code: fall through to the plain number formatter.
    }
  }

  return new Intl.NumberFormat(overrideLocale, digits);
}

export function formatMoney(x: number, currency = currentCurrency, overrideLocale = locale) {
  if (Number.isNaN(x)) return '';

  return moneyFormatter(currency, overrideLocale, { minimumFractionDigits: 2 }).format(x);
}

export function formatWholeMoney(x: number, currency = currentCurrency, overrideLocale = locale) {
  if (Number.isNaN(x)) return '';

  return moneyFormatter(currency, overrideLocale, { maximumFractionDigits: 0 }).format(x);
}

export function formatNumber(x: number, overrideLocale = locale) {
  if (Number.isNaN(x)) return '';

  const formatter = new Intl.NumberFormat(overrideLocale, {
    maximumFractionDigits: 2,
  });

  return formatter.format(x);
}

export function formatDecimalNumber(x: number, overrideLocale = locale) {
  if (Number.isNaN(x)) return '';

  const formatter = new Intl.NumberFormat(overrideLocale, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });

  return formatter.format(x);
}

export function formatPercentage(x: number, overrideLocale = locale) {
  if (Number.isNaN(x)) return '';

  const formatter = new Intl.NumberFormat(overrideLocale, {
    style: 'percent',
    maximumFractionDigits: 2,
  });

  return formatter.format(centsToDecimal(x));
}

export function formatCentsPercentage(x: number, overrideLocale = locale) {
  if (Number.isNaN(x)) return '';

  return formatPercentage(centsToDecimal(x), overrideLocale);
}

export function getValueOrPlaceholder(value: any, placeholder: string) {
  return value || placeholder;
}

export function formatCents(
  x: number | null | undefined,
  currency = currentCurrency,
  overrideLocale = locale,
) {
  return formatMoney(centsToDecimal(x), currency, overrideLocale);
}

export function numberOrZero(x: number | undefined | null) {
  if (typeof x === 'undefined' || x === null || Number.isNaN(x) || !Number.isFinite(x)) return 0;

  return x;
}

export function decimalToCents(x: number | undefined | null) {
  if (typeof x === 'undefined' || x === null || Number.isNaN(x) || !Number.isFinite(x)) return 0;

  // Cents are integers on the API; sub-cent input rounds half up (away from zero).
  // `+ 0` folds the negative zero a tiny negative input would otherwise produce.
  const dec = new Decimal(x);
  return dec.mul(100).round().toNumber() + 0;
}

export function centsToDecimal(x: number | undefined | null) {
  if (typeof x === 'undefined' || x === null || Number.isNaN(x) || !Number.isFinite(x)) return 0;

  const dec = new Decimal(x);
  return dec.div(100).toNumber();
}

export function deslugify(s: unknown) {
  if (!s || typeof s !== 'string') return `${s}`;

  return s.replace(/-/g, ' ');
}

export function zeroPad(num: number, places: number) {
  return String(num).padStart(places, '0');
}

export function pluralize(num: number, noun: string, plural?: string) {
  if (num === 1) {
    return noun;
  }
  // Irregular plurals ("category" -> "categories") can't be formed by appending.
  return plural ?? `${noun}s`;
}

export function upcaseFirst(s: string | undefined) {
  if (!s) return '';

  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

export function truncateWithEllipsis(s: string, maxLength: number) {
  if (s.length <= maxLength) return s;

  return `${s.slice(0, maxLength)}…`;
}
